import { useEffect, useState } from 'react';
import {
    InputText,
    PrimaryButton,
    SecondaryButton,
    Select,
} from '../../../shared/ui/Inputs';
import { ErrorMessage } from '../../../shared/messages/messages';
import { createCompany, editCompany } from '../../../services/configuration/companiesService';
import { invalidateCompaniesCatalog } from '../../../hooks/useCompaniesCatalog';

const NOTA_FISCAL_OPTIONS = ['REMESSA', 'SIMPLES FATURAMENTO', 'VENDA'];
const PRAZO_FIELDS = [
    { key: 'zaeq', label: 'Prazo de implantação' },
    { key: 'zrec', label: 'Recolhimento' },
    { key: 'zvth', label: 'Assistência Técnica' },
    { key: 'zven_descartavel', label: 'Prazo de reposição de descartável' },
    { key: 'zven_recarga', label: 'Prazo de Recarga' },
];

const getPrazosState = (company = {}) =>
    PRAZO_FIELDS.reduce((result, { key }) => {
        result[key] = company[key] ?? '';
        return result;
    }, {});

export const CompaniesForm = ({
    id,
    cnpj,
    empresa,
    bp,
    divisao,
    nota_fiscal,
    zaeq,
    zrec,
    zvth,
    zven_descartavel,
    zven_recarga,
    onAction,
    isEditing,
}) => {
    const [_cnpj, setCNPJ] = useState(
        (cnpj ?? '').toString().replace(/\D/g, '').slice(0, 14)
    );
    const [_empresa, setEmpresa] = useState(empresa ?? '');
    const [_bp, setBp] = useState((bp ?? '').toString().slice(0, 20));
    const [_divisao, setDivisao] = useState(
        (divisao ?? '40').toString() === '90' ? '90' : '40'
    );
    const [_notaFiscal, setNotaFiscal] = useState(
        NOTA_FISCAL_OPTIONS.includes(nota_fiscal) ? nota_fiscal : ''
    );
    const [prazos, setPrazos] = useState(
        getPrazosState({ zaeq, zrec, zvth, zven_descartavel, zven_recarga })
    );
    const [message, setMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        setCNPJ((cnpj ?? '').toString().replace(/\D/g, '').slice(0, 14));
        setEmpresa(empresa ?? '');
        setBp((bp ?? '').toString().slice(0, 20));
        setDivisao((divisao ?? '40').toString() === '90' ? '90' : '40');
        setNotaFiscal(
            NOTA_FISCAL_OPTIONS.includes(nota_fiscal)
                ? nota_fiscal
                : ''
        );
        setPrazos(getPrazosState({ zaeq, zrec, zvth, zven_descartavel, zven_recarga }));
    }, [cnpj, empresa, bp, divisao, nota_fiscal, zaeq, zrec, zvth, zven_descartavel, zven_recarga]);

    const validateForm = () => {
        if (!_empresa.trim()) {
            setMessage('Nome da empresa é obrigatório');
            return false;
        }
        if (!_cnpj.trim()) {
            setMessage('CNPJ é obrigatório');
            return false;
        }
        // Validate CNPJ with exactly 14 numeric digits
        if (!/^\d{14}$/.test(_cnpj)) {
            setMessage('CNPJ deve conter exatamente 14 números');
            return false;
        }
        if (!_bp.trim()) {
            setMessage('BP é obrigatório para vincular os prazos da empresa');
            return false;
        }
        if (!/^\d{1,20}$/.test(_bp.trim())) {
            setMessage('BP deve conter entre 1 e 20 números');
            return false;
        }
        return true;
    };

    const handleSubmit = async () => {
        setMessage('');
        if (!validateForm()) return;

        setIsSubmitting(true);
        try {
            const companyData = {
                cnpj: _cnpj.trim().replace(/\D/g, ''),
                empresa: _empresa.trim(),
                bp: _bp.trim() ? _bp.trim() : null,
                divisao: _divisao,
                nota_fiscal: _notaFiscal || null,
                ...prazos,
            };

            if (isEditing) {
                await editCompany(id, companyData);
            } else {
                await createCompany(companyData);
            }

            invalidateCompaniesCatalog();

            onAction?.();
        } catch (error) {
            console.warn('Erro ao salvar empresa:', error);
            setMessage(error?.message || 'Erro ao salvar a empresa');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div
            style={{
                padding: '15px',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <div style={{ flex: 1 }}>
                <div style={{ marginBottom: '20px' }}>
                    <label
                        style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}
                    >
                        Nome da Empresa *
                    </label>
                    <InputText
                        style={{ width: '100%' }}
                        value={_empresa}
                        onChange={(e) => setEmpresa(e.target.value)}
                        placeholder="Digite o nome da empresa"
                        disabled={isSubmitting}
                    />
                </div>

                <div style={{ marginBottom: '20px' }}>
                    <label
                        style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}
                    >
                        CNPJ *
                    </label>
                    <InputText
                        style={{ width: '100%' }}
                        value={_cnpj}
                        onChange={(e) => setCNPJ(e.target.value)}
                        placeholder="Digite o CNPJ (somente números)"
                        disabled={isSubmitting}
                    />
                    <small style={{ color: '#666' }}>{_cnpj.length}/14</small>
                </div>

                <div style={{ marginBottom: '20px' }}>
                    <label
                        style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}
                    >
                        BP
                    </label>
                    <InputText
                        style={{ width: '100%' }}
                        value={_bp}
                        onChange={(e) => setBp((e.target.value ?? '').replace(/\D/g, '').slice(0, 20))}
                        placeholder="Digite o BP"
                        disabled={isSubmitting}
                    />
                    <small style={{ color: '#666' }}>{_bp.length}/20</small>
                </div>

                <div style={{ marginBottom: '20px' }}>
                    <label
                        style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}
                    >
                        Divisão *
                    </label>
                    <Select
                        style={{ width: '100%' }}
                        value={_divisao}
                        onChange={(e) => setDivisao(e.target.value)}
                        disabled={isSubmitting}
                    >
                        <option value="40">40</option>
                        <option value="90">90</option>
                    </Select>
                </div>

                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                        gap: '14px',
                        marginBottom: '20px',
                    }}
                >
                    {PRAZO_FIELDS.map(({ key, label }) => (
                        <div key={key}>
                            <label
                                style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}
                            >
                                {label}
                            </label>
                            <InputText
                                style={{ width: '100%' }}
                                value={prazos[key] ?? ''}
                                maxLength={255}
                                onChange={(e) =>
                                    setPrazos((current) => ({
                                        ...current,
                                        [key]: e.target.value,
                                    }))
                                }
                                placeholder="Ex.: 24h ou Não possui consumo de gás"
                                disabled={isSubmitting}
                            />
                        </div>
                    ))}
                </div>

                <div style={{ marginBottom: '20px' }}>
                    <label
                        style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}
                    >
                        NF
                    </label>
                    <Select
                        style={{ width: '100%' }}
                        value={_notaFiscal}
                        onChange={(e) => setNotaFiscal(e.target.value)}
                        disabled={isSubmitting}
                    >
                        <option value="">Selecione</option>
                        {NOTA_FISCAL_OPTIONS.map((option) => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </Select>
                </div>

                {message ? <ErrorMessage>{message}</ErrorMessage> : null}
            </div>

            <div
                style={{
                    display: 'flex',
                    gap: '10px',
                    justifyContent: 'flex-end',
                    flexShrink: 0,
                }}
            >
                <SecondaryButton
                    disabled={isSubmitting}
                    onClick={() => onAction()}
                    style={{ width: '100px' }}
                >
                    Cancelar
                </SecondaryButton>
                <PrimaryButton
                    disabled={isSubmitting}
                    onClick={handleSubmit}
                    style={{ width: '100px' }}
                >
                    {isSubmitting ? 'Salvando...' : 'Salvar'}
                </PrimaryButton>
            </div>
        </div>
    );
};
